/**
 * Terminal-condition checks (rule.md §4): breakthrough win, elimination loss,
 * and turn-cap scoring with the strict tie-break.
 */
import { type CellId, HOME_CELLS, hexDistance } from "../data/board";
import { PLAYER_IDS, type PlayerId } from "../types/common";
import type { AugmentedWarState, Board, GameResult } from "../types/game-state";
import {
  cellsOwnedBy,
  countUnitsOnBoard,
  hasPiecesLeft,
  opponentOf,
  totalHpOnBoard,
} from "./state";

/** A breakthrough: `player` has a unit on one of the enemy's home cells. */
function breakthroughWinner(board: Board): PlayerId | undefined {
  for (const player of PLAYER_IDS) {
    const enemyHomes = HOME_CELLS[opponentOf(player)];
    for (const home of enemyHomes) {
      if (board[home]?.owner === player) return player;
    }
  }
  return undefined;
}

/** Elimination: a player with no board units and no reserve loses. */
function eliminationWinner(state: AugmentedWarState): PlayerId | undefined {
  for (const player of PLAYER_IDS) {
    if (!hasPiecesLeft(state, player)) return opponentOf(player);
  }
  return undefined;
}

/**
 * Immediate terminal result after an action, or undefined if the game continues.
 * Breakthrough takes precedence over elimination.
 */
export function checkTerminal(
  state: AugmentedWarState,
): GameResult | undefined {
  const bt = breakthroughWinner(state.board);
  if (bt) return { kind: "breakthrough", winner: bt };
  const el = eliminationWinner(state);
  if (el) return { kind: "elimination", winner: el };
  return undefined;
}

/** Closest hex-distance from any of `player`'s units to an enemy home cell. */
function closestToEnemyHome(board: Board, player: PlayerId): number {
  const enemyHomes = HOME_CELLS[opponentOf(player)];
  let best = Number.POSITIVE_INFINITY;
  for (const cell of cellsOwnedBy(board, player)) {
    for (const home of enemyHomes) {
      const d = hexDistance(cell as CellId, home);
      if (d < best) best = d;
    }
  }
  return best;
}

/** Turn-cap scoring (rule.md §4): closest-to-home → HP → stacks → stalemate. */
export function scoreTimeout(state: AugmentedWarState): GameResult {
  const { board } = state;
  const dist: Record<PlayerId, number> = {
    p1: closestToEnemyHome(board, "p1"),
    p2: closestToEnemyHome(board, "p2"),
  };
  if (dist.p1 !== dist.p2) {
    return { kind: "timeout", winner: dist.p1 < dist.p2 ? "p1" : "p2" };
  }
  const hp: Record<PlayerId, number> = {
    p1: totalHpOnBoard(board, "p1"),
    p2: totalHpOnBoard(board, "p2"),
  };
  if (hp.p1 !== hp.p2) {
    return { kind: "timeout", winner: hp.p1 > hp.p2 ? "p1" : "p2" };
  }
  const stacks: Record<PlayerId, number> = {
    p1: countUnitsOnBoard(board, "p1"),
    p2: countUnitsOnBoard(board, "p2"),
  };
  if (stacks.p1 !== stacks.p2) {
    return { kind: "timeout", winner: stacks.p1 > stacks.p2 ? "p1" : "p2" };
  }
  return { kind: "timeout", winner: null };
}
